/**
 * H5 听者 TTS：定稿字幕经 Azure SpeechSynthesizer（WS requestSpeechToken tts + cache）
 * D9 FIFO · D10 不回溯 · D11 stopAndClear + generation · 默认关
 */

import { ref } from 'vue'
import { fetchRuntimeConfig } from '../api/cr-v3-viewer.api'
import { buildTtsSsml } from '../utils/azure-neural-voice'
import type { RequestSpeechTokenResult } from './useCrV3H5Ws'

export interface UseCrV3H5TtsOptions {
  requestToken: (
    intent: 'tts',
    languageCode?: string,
    opts?: { allowCache?: boolean },
  ) => Promise<RequestSpeechTokenResult>
}

type QueueItem = { text: string; locale: string }

/** WS token + runtime-config 语速 */
export function useCrV3H5Tts(options: UseCrV3H5TtsOptions) {
  const playbackRate = ref(1)
  /** D1：默认关 */
  const enabled = ref(false)
  let generation = 0
  const queue: QueueItem[] = []
  let draining = false

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let synthesizer: any = null
  let tokenPair: { token: string; region: string } | null = null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let sdkModule: any = null

  async function loadRuntimeConfig(workId?: string) {
    try {
      const cfg = await fetchRuntimeConfig(workId)
      if (typeof cfg.ttsPlaybackRate === 'number' && cfg.ttsPlaybackRate > 0) {
        playbackRate.value = cfg.ttsPlaybackRate
      }
    } catch {
      // 缺省 1.0
    }
  }

  /** 会中若仍收到 updateRuntimeConfig（遗留），仅更新倍速；主路径为 start 冻结快照 */
  function applyPlaybackRate(rate: number) {
    if (typeof rate === 'number' && rate > 0) {
      playbackRate.value = rate
    }
  }

  function stopAndClear(keepEnabled = true) {
    generation += 1
    queue.length = 0
    if (!keepEnabled) {
      enabled.value = false
    }
    try {
      synthesizer?.close?.()
    } catch {
      // ignore
    }
    synthesizer = null
    // token cache 保留（切语可复用）
  }

  function setEnabled(value: boolean) {
    if (!value) {
      stopAndClear(false)
      return
    }
    enabled.value = true
    // D10：不回溯；仅允许此后 speak
  }

  async function ensureSynthesizer(languageCode: string) {
    if (synthesizer && tokenPair) {
      return synthesizer
    }
    const result = await options.requestToken('tts', languageCode, { allowCache: true })
    if (!result.ok) {
      throw new Error(`TTS token unavailable (${result.busy.reason || 'unknown'})`)
    }
    const { token, region } = result.data
    tokenPair = { token, region }
    const speechsdk = await import('microsoft-cognitiveservices-speech-sdk')
    sdkModule = speechsdk
    const config = speechsdk.SpeechConfig.fromAuthorizationToken(token, region)
    const audioConfig = speechsdk.AudioConfig.fromDefaultSpeakerOutput()
    synthesizer = new speechsdk.SpeechSynthesizer(config, audioConfig)
    return synthesizer
  }

  function buildSsml(text: string, languageCode2: string): string {
    return buildTtsSsml(text, languageCode2, playbackRate.value)
  }

  async function speakOne(item: QueueItem, myGen: number) {
    try {
      const synth = await ensureSynthesizer(item.locale)
      if (myGen !== generation || !enabled.value) {
        try {
          synth?.close?.()
        } catch {
          // ignore
        }
        if (synthesizer === synth) {
          synthesizer = null
        }
        return
      }
      const ssml = buildSsml(item.text, item.locale)
      await new Promise<void>((resolve, reject) => {
        if (myGen !== generation || !enabled.value) {
          resolve()
          return
        }
        synth.speakSsmlAsync(
          ssml,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (result: any) => {
            // Azure SDK：Canceled 走成功回调，须显式检查，否则静默无声
            const canceledReason = sdkModule?.ResultReason?.Canceled
            if (result != null && canceledReason != null && result.reason === canceledReason) {
              reject(new Error(result.errorDetails || 'TTS synthesis canceled'))
              return
            }
            resolve()
          },
          (err: string) => reject(new Error(err)),
        )
      })
      if (myGen !== generation && synthesizer === synth) {
        try {
          synth?.close?.()
        } catch {
          // ignore
        }
        synthesizer = null
      }
    } catch {
      // 合成失败不抛出；队列继续播下一句
    }
  }

  async function drainQueue() {
    if (draining) {
      return
    }
    draining = true
    try {
      while (queue.length > 0 && enabled.value) {
        const myGen = generation
        const item = queue.shift()!
        await speakOne(item, myGen)
        if (myGen !== generation) {
          break
        }
      }
    } finally {
      draining = false
      if (queue.length > 0 && enabled.value) {
        void drainQueue()
      }
    }
  }

  /** D9 FIFO：只入队，不打断当前句 */
  function speak(text: string, languageCode2: string) {
    const trimmed = text.trim()
    if (!enabled.value || !trimmed) {
      return
    }
    queue.push({ text: trimmed, locale: languageCode2 })
    void drainQueue()
  }

  function close() {
    stopAndClear(false)
    tokenPair = null
  }

  return {
    speak,
    loadRuntimeConfig,
    applyPlaybackRate,
    playbackRate,
    enabled,
    setEnabled,
    stopAndClear,
    close,
  }
}
